import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Eye, Send, CheckCircle, Mail, Phone, MapPin, Users, Camera, Paperclip, X, Lock, Trash2, Edit2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useWebSocket } from "@/context/WebSocketContext";

import RichTextEditor, { RichTextDisplay } from "@/components/RichTextEditor";

const fmt_time = (d) => {
  if (!d) return "";
  const diff = Date.now() - new Date(d).getTime();
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return new Date(d).toLocaleDateString();
};

function AgencyNewThreadDialog({ clientId, onClose, onCreated }) {
  const [subject, setSubject] = React.useState("");
  const [requestId, setRequestId] = React.useState("");
  const [userId, setUserId] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [attachments, setAttachments] = React.useState([]);
  const [requests, setRequests] = React.useState([]);
  const [clientUsers, setClientUsers] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const fileRef = React.useRef(null);

  React.useEffect(() => {
    api.get(`/threads/requests-for-client?client_id=${clientId}`).then((r) => setRequests(r.data)).catch(() => {});
    api.get(`/clients/${clientId}/users`).then((r) => setClientUsers(r.data?.filter((u) => u.role?.startsWith("client_")) || [])).catch(() => {});
  }, [clientId]);

  const handleFile = (e) => {
    Array.from(e.target.files || []).forEach((file) => {
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} too large`); return; }
      const r = new FileReader();
      r.onload = (ev) => {
        const b64 = ev.target.result.split(",")[1];
        setAttachments((p) => [...p, { filename: file.name, content_type: file.type, data_b64: b64 }]);
      };
      r.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim()) { toast.error("Subject is required"); return; }
    setSaving(true);
    try {
      const r = await api.post("/threads", {
        subject: subject.trim(),
        client_id: clientId,
        related_request_id: requestId || null,
        is_org_wide: !!requestId,
        private_user_id: !requestId && userId ? userId : null,
        initial_message: message || null,
        initial_attachments: attachments,
      });
      onCreated(r.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create thread");
    } finally {
      setSaving(false);
    }
  };

  const isOrgWide = !!requestId;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.4)" }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-6" data-testid="agency-new-thread-dialog">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif text-xl font-bold" style={{ color: "#292524" }}>New Conversation</h2>
          <button onClick={onClose} style={{ color: "#A8A29E" }}><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#57534E" }}>Subject *</Label>
            <Input autoFocus required value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="What's this about?" className="mt-1.5" data-testid="agency-thread-subject" />
          </div>
          {requests.length > 0 && (
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#57534E" }}>Link to a Task (optional)</Label>
              <select value={requestId} onChange={(e) => { setRequestId(e.target.value); if (e.target.value) { const req = requests.find((r) => r.id === e.target.value); if (req && !subject) setSubject(req.title); } }} className="mt-1.5 w-full px-3 py-2 rounded-lg border text-sm outline-none" style={{ borderColor: "#E7E5E4" }} data-testid="agency-thread-request-select">
                <option value="">None — private conversation</option>
                {requests.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
              </select>
              {isOrgWide && <p className="text-xs mt-1.5 flex items-center gap-1" style={{ color: "#D97706" }}><Users size={11} />This will be visible to all team members of this client.</p>}
            </div>
          )}
          {!isOrgWide && clientUsers.length > 0 && (
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#57534E" }}>Send to (optional)</Label>
              <select value={userId} onChange={(e) => setUserId(e.target.value)} className="mt-1.5 w-full px-3 py-2 rounded-lg border text-sm outline-none" style={{ borderColor: "#E7E5E4" }} data-testid="agency-thread-user-select">
                <option value="">All client users can see</option>
                {clientUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider mb-1.5 block" style={{ color: "#57534E" }}>Opening Message (optional)</Label>
            <RichTextEditor value={message} onChange={setMessage} placeholder="Start the conversation..." minHeight={72} />
          </div>
          {attachments.length > 0 && (
            <div className="space-y-1">
              {attachments.map((att, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ background: "#F5F3EF" }}>
                  <span className="text-xs truncate mr-2" style={{ color: "#292524" }}>{att.filename}</span>
                  <button type="button" onClick={() => setAttachments((p) => p.filter((_, j) => j !== i))} style={{ color: "#A8A29E" }}><X size={12} /></button>
                </div>
              ))}
            </div>
          )}
          <input ref={fileRef} type="file" multiple className="hidden" onChange={handleFile} />
          <div className="flex items-center justify-between pt-1">
            <button type="button" onClick={() => fileRef.current?.click()} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border hover:bg-stone-50" style={{ borderColor: "#E7E5E4", color: "#57534E" }}>
              <Paperclip size={12} />Attach
            </button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
              <Button type="submit" size="sm" disabled={saving} style={{ background: "#292524", color: "#fff" }} data-testid="agency-create-thread-btn">{saving ? "Creating..." : "Create"}</Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function AgencyThreadConversation({ thread }) {
  const { subscribe } = useWebSocket();
  const [messages, setMessages] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [content, setContent] = React.useState("");
  const [attachments, setAttachments] = React.useState([]);
  const [sending, setSending] = React.useState(false);
  const endRef = React.useRef(null);
  const fileRef = React.useRef(null);

  React.useEffect(() => {
    setLoading(true);
    api.get(`/threads/${thread.id}/messages`).then((r) => setMessages(r.data)).finally(() => setLoading(false));
  }, [thread.id]);

  // Subscribe to WS — refresh messages when client sends to this thread
  React.useEffect(() => {
    const unsub = subscribe((event) => {
      if (event.type === "new_message" && event.thread_id === thread.id && event.sender_type === "client") {
        api.get(`/threads/${thread.id}/messages`).then((r) => setMessages(r.data)).catch(() => {});
      }
    });
    return unsub;
  }, [subscribe, thread.id]);

  React.useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const handleFile = (e) => {
    Array.from(e.target.files || []).forEach((file) => {
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} too large`); return; }
      const r = new FileReader();
      r.onload = (ev) => { const b64 = ev.target.result.split(",")[1]; setAttachments((p) => [...p, { filename: file.name, content_type: file.type, data_b64: b64 }]); };
      r.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const send = async () => {
    if (!content.trim() && attachments.length === 0) return;
    setSending(true);
    try {
      const r = await api.post(`/threads/${thread.id}/messages`, { content, attachments });
      setMessages((p) => [...p, r.data]);
      setContent(""); setAttachments([]);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send"); }
    finally { setSending(false); }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 flex-shrink-0" style={{ borderBottom: "1px solid #E7E5E4" }}>
        <p className="text-sm font-semibold" style={{ color: "#292524" }}>{thread.subject}</p>
        {thread.related_request_title && <p className="text-xs mt-0.5" style={{ color: "#A8A29E" }}>Re: {thread.related_request_title}</p>}
        <div className="flex items-center gap-2 mt-1">
          {thread.is_org_wide
            ? <span className="text-xs flex items-center gap-1" style={{ color: "#D97706" }}><Users size={9} />Team visible</span>
            : <span className="text-xs flex items-center gap-1" style={{ color: "#A8A29E" }}><Lock size={9} />Private</span>}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-60">
        {loading ? <p className="text-xs text-center py-4" style={{ color: "#A8A29E" }}>Loading...</p>
          : messages.length === 0 ? <p className="text-xs text-center py-4" style={{ color: "#A8A29E" }}>No messages yet</p>
          : messages.map((msg) => {
            const isAgency = msg.sender_type === "agency";
            return (
              <div key={msg.id} className="flex gap-2.5 items-start">
                <div className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-semibold" style={{ background: isAgency ? "#292524" : "#D97706" }}>
                  {msg.sender_name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 mb-0.5">
                    <span className="text-xs font-semibold" style={{ color: "#292524" }}>{msg.sender_name}</span>
                    <span className="text-xs" style={{ color: "#C7C3BF" }}>{fmt_time(msg.created_at)}</span>
                  </div>
                  <div className="rounded-lg px-3 py-2" style={{ background: "#F5F3EF", border: "1px solid #E7E5E4" }}>
                    <RichTextDisplay html={msg.content} />
                    {msg.attachments?.length > 0 && (
                      <div className="mt-1.5 space-y-1">
                        {msg.attachments.map((att, i) => (
                          <a key={i} href={`data:${att.content_type};base64,${att.data_b64}`} download={att.filename} className="flex items-center gap-1 text-xs" style={{ color: "#0284C7" }}>
                            <Paperclip size={10} />{att.filename}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        <div ref={endRef} />
      </div>
      <div className="flex-shrink-0 p-3" style={{ borderTop: "1px solid #E7E5E4" }}>
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {attachments.map((att, i) => (
              <div key={i} className="flex items-center gap-1 px-2 py-0.5 rounded text-xs" style={{ background: "#F5F3EF" }}>
                <Paperclip size={9} /><span className="max-w-[80px] truncate">{att.filename}</span>
                <button onClick={() => setAttachments((p) => p.filter((_, j) => j !== i))} style={{ color: "#A8A29E" }}><X size={9} /></button>
              </div>
            ))}
          </div>
        )}
        <RichTextEditor value={content} onChange={setContent} placeholder="Write a reply..." minHeight={48} />
        <div className="flex items-center justify-between mt-2">
          <div>
            <input ref={fileRef} type="file" multiple className="hidden" onChange={handleFile} />
            <button type="button" onClick={() => fileRef.current?.click()} className="flex items-center gap-1 text-xs px-2 py-1.5 rounded border hover:bg-stone-50" style={{ borderColor: "#E7E5E4", color: "#57534E" }}><Paperclip size={11} />Attach</button>
          </div>
          <Button size="sm" disabled={sending || (!content.trim() && attachments.length === 0)} onClick={send} style={{ background: "#292524", color: "#fff" }} data-testid="agency-thread-send">
            <Send size={12} className="mr-1" />Reply
          </Button>
        </div>
      </div>
    </div>
  );
}

function ClientMessagesTab({ clientId }) {
  const { subscribe } = useWebSocket();
  const [threads, setThreads] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState(null);
  const [showNew, setShowNew] = React.useState(false);
  const [deletingId, setDeletingId] = React.useState(null);
  const [mobileView, setMobileView] = React.useState("list"); // "list" | "conversation"

  const loadThreads = React.useCallback(() => {
    api.get(`/threads?client_id=${clientId}`)
      .then((r) => { setThreads(r.data); if (r.data.length > 0) setSelected((prev) => prev || r.data[0]); })
      .finally(() => setLoading(false));
  }, [clientId]);

  React.useEffect(() => { loadThreads(); }, [loadThreads]);

  // Subscribe to WS — refresh thread list when client sends a message for this client
  React.useEffect(() => {
    const unsub = subscribe((event) => {
      if (event.type === "new_message" && event.client_id === clientId && event.sender_type === "client") {
        api.get(`/threads?client_id=${clientId}`).then((r) => setThreads(r.data)).catch(() => {});
      }
    });
    return unsub;
  }, [subscribe, clientId]);

  const onCreated = (t) => { setThreads((p) => [t, ...p]); setSelected(t); };

  const handleDelete = async (threadId) => {
    try {
      await api.delete(`/threads/${threadId}`);
      setThreads((p) => p.filter((t) => t.id !== threadId));
      if (selected?.id === threadId) { setSelected(null); setMobileView("list"); }
      toast.success("Conversation deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) return <p className="text-sm text-center py-8" style={{ color: "#A8A29E" }}>Loading...</p>;

  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button size="sm" onClick={() => setShowNew(true)} style={{ background: "#292524", color: "#fff" }} data-testid="agency-new-thread-button">
          <Plus size={13} className="mr-1" />New Conversation
        </Button>
      </div>
      <div className="rounded-xl border flex flex-col md:flex-row overflow-hidden" style={{ borderColor: "#E7E5E4", minHeight: "480px" }}>
        {/* Thread list */}
        <div className={`${mobileView === "list" ? "flex flex-col" : "hidden"} md:flex md:flex-col w-full md:w-56 flex-shrink-0 overflow-y-auto`} style={{ borderRight: "1px solid #E7E5E4" }}>
          {threads.length === 0 ? (
            <p className="text-xs text-center py-8" style={{ color: "#A8A29E" }}>No conversations yet</p>
          ) : threads.map((t) => (
            <div key={t.id} className="relative group" style={{ borderBottom: "1px solid #E7E5E4" }}>
              <button
                onClick={() => setSelected(t)}
                className="w-full text-left px-3 py-3 transition-colors pr-10"
                style={{ background: selected?.id === t.id ? "#F5F3EF" : "transparent" }}
                data-testid={`agency-thread-${t.id}`}
              >
                <p className={`text-xs flex-1 truncate ${t.unread_by_agency ? "font-semibold" : "font-medium"}`} style={{ color: "#292524" }}>
                  {t.unread_by_agency && <span className="inline-block w-1.5 h-1.5 rounded-full mr-1 mb-0.5" style={{ background: "#D97706" }} />}
                  {t.subject}
                </p>
                <p className="text-xs truncate mt-0.5" style={{ color: "#A8A29E" }}>{t.last_message_preview || "No messages"}</p>
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-xs" style={{ color: "#C7C3BF" }}>{fmt_time(t.last_message_at)}</span>
                  {t.is_org_wide && <span className="text-xs" style={{ color: "#D97706" }}>· Team</span>}
                </div>
              </button>
              {/* Delete button */}
              {deletingId === t.id ? (
                <div className="absolute right-1 top-2.5 flex items-center gap-1 z-10">
                  <button
                    onClick={() => handleDelete(t.id)}
                    className="text-xs px-2 py-1 rounded-lg font-medium"
                    style={{ background: "#FEE2E2", color: "#DC2626" }}
                    data-testid={`agency-confirm-delete-${t.id}`}
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => setDeletingId(null)}
                    className="text-xs px-1.5 py-1 rounded-lg"
                    style={{ background: "#F5F3EF", color: "#78716C" }}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={(e) => { e.stopPropagation(); setDeletingId(t.id); }}
                  className="absolute right-1.5 top-2.5 p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ color: "#C7C3BF" }}
                  data-testid={`agency-delete-thread-${t.id}`}
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
        {/* Conversation */}
        <div className="flex-1 min-w-0" style={{ background: "#FAFAF9" }}>
          {selected ? (
            <AgencyThreadConversation key={selected.id} thread={selected} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm" style={{ color: "#A8A29E" }}>Select a conversation</p>
            </div>
          )}
        </div>
      </div>
      {showNew && <AgencyNewThreadDialog clientId={clientId} onClose={() => setShowNew(false)} onCreated={onCreated} />}
    </div>
  );
}

const fmt = (s) => s?.split("_").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ") || "";
const statusColors = {
  pending_review: { bg: "#FEF3C7", text: "#D97706" },
  in_progress: { bg: "#DBEAFE", text: "#1D4ED8" },
  completed: { bg: "#D1FAE5", text: "#059669" },
  cancelled: { bg: "#FEE2E2", text: "#DC2626" },
  on_hold: { bg: "#F3E8FF", text: "#7C3AED" },
};
const cycleStatusColors = {
  active: { bg: "#D1FAE5", text: "#059669" },
  closed: { bg: "#F5F3EF", text: "#57534E" },
  invoiced: { bg: "#DBEAFE", text: "#1D4ED8" },
};

const FieldBlock = ({ label, children }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "#A8A29E" }}>{label}</p>
    {children}
  </div>
);
const inpCls = "w-full px-3 py-2 rounded-lg border text-sm outline-none focus:ring-1 focus:ring-amber-300";
const inpStyle = { borderColor: "#E7E5E4", background: "#fff", color: "#292524" };

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({});
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [userForm, setUserForm] = useState({ name: "", email: "", password: "", role: "client_user", send_welcome_email: false });
  const [saving, setSaving] = useState(false);
  const logoRef = useRef(null);
  const [clientMessages, setClientMessages] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [sendingMsg, setSendingMsg] = useState(false);
  const [editUser, setEditUser] = useState(null); // { id, name, email, role }
  const [editUserForm, setEditUserForm] = useState({ name: "", email: "", role: "client_user", password: "" });
  const [editUserSaving, setEditUserSaving] = useState(false);
  const [deleteUserTarget, setDeleteUserTarget] = useState(null);
  const [deleteInvoiceTarget, setDeleteInvoiceTarget] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`/clients/${id}`),
      api.get(`/clients/${id}/users`),
      api.get(`/requests?client_id=${id}`),
      api.get(`/billing/cycles?client_id=${id}`),
      api.get("/team"),
      api.get(`/invoices?client_id=${id}`),
      api.get(`/clients/${id}/messages`),
    ])
      .then(([c, u, r, cyc, t, inv, msgs]) => {
        setClient(c.data);
        setForm({
          name: c.data.name, email: c.data.email, phone: c.data.phone || "",
          address: c.data.address || "", client_manager_id: c.data.client_manager_id || "",
          hourly_rate: c.data.billing_config?.hourly_rate ?? 0,
          retained_hours: c.data.billing_config?.retained_hours ?? 0,
          additional_rate: c.data.billing_config?.additional_rate ?? "",
          billing_cycle_day: c.data.billing_config?.billing_cycle_day ?? 1,
          logo_image: c.data.logo_image || "",
          invoice_sender_name: c.data.billing_config?.invoice_sender_name || "",
          ba_line1: c.data.billing_address?.line1 || "",
          ba_line2: c.data.billing_address?.line2 || "",
          ba_city: c.data.billing_address?.city || "",
          ba_postcode: c.data.billing_address?.postcode || "",
          ba_country: c.data.billing_address?.country || "UK",
        });
        setUsers(u.data);
        setRequests(r.data);
        setCycles(cyc.data);
        setTeam(t.data);
        setInvoices(inv.data);
        setClientMessages(msgs.data);
      })
      .catch(() => navigate("/clients"))
      .finally(() => setLoading(false));
  }, [id]);

  const saveBasic = async (updates) => {
    try {
      const r = await api.put(`/clients/${id}`, updates);
      setClient(r.data);
    } catch { toast.error("Failed to save"); }
  };

  const saveBilling = async () => {
    await saveBasic({
      billing_config: {
        hourly_rate: parseFloat(form.hourly_rate) || 0,
        retained_hours: parseFloat(form.retained_hours) || 0,
        additional_rate: form.additional_rate ? parseFloat(form.additional_rate) : null,
        billing_cycle_day: parseInt(form.billing_cycle_day) || 1,
        invoice_sender_name: form.invoice_sender_name || null,
      },
    });
    toast.success("Billing config saved");
  };

  const saveBillingAddress = async () => {
    await saveBasic({
      billing_address: {
        line1: form.ba_line1,
        line2: form.ba_line2,
        city: form.ba_city,
        postcode: form.ba_postcode,
        country: form.ba_country,
      },
    });
    toast.success("Billing address saved");
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2 MB"); return; }
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const logoImage = ev.target.result;
      setForm((f) => ({ ...f, logo_image: logoImage }));
      await saveBasic({ logo_image: logoImage });
      toast.success("Logo updated");
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post(`/clients/${id}/users`, userForm);
      setUsers((prev) => [...prev, r.data]);
      setShowUserDialog(false);
      setUserForm({ name: "", email: "", password: "", role: "client_user", send_welcome_email: false });
      toast.success("Client user added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add user");
    } finally {
      setSaving(false);
    }
  };

  const openEditUser = (u) => {
    setEditUser(u);
    setEditUserForm({ name: u.name, email: u.email, role: u.role, password: "" });
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setEditUserSaving(true);
    try {
      const payload = { name: editUserForm.name, email: editUserForm.email, role: editUserForm.role };
      if (editUserForm.password) payload.password = editUserForm.password;
      const r = await api.put(`/clients/${id}/users/${editUser.id}`, payload);
      setUsers((prev) => prev.map((u) => u.id === editUser.id ? r.data : u));
      setEditUser(null);
      toast.success("User updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update user");
    } finally {
      setEditUserSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserTarget) return;
    try {
      await api.delete(`/clients/${id}/users/${deleteUserTarget.id}`);
      setUsers((prev) => prev.filter((u) => u.id !== deleteUserTarget.id));
      toast.success("User removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove user");
    } finally {
      setDeleteUserTarget(null);
    }
  };

  const handleDeleteInvoice = async () => {
    if (!deleteInvoiceTarget) return;
    try {
      await api.delete(`/invoices/${deleteInvoiceTarget.id}`);
      setInvoices((prev) => prev.filter((i) => i.id !== deleteInvoiceTarget.id));
      toast.success("Invoice deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete invoice");
    } finally {
      setDeleteInvoiceTarget(null);
    }
  };

  const markInvoicePaid = async (invId) => {    try {
      await api.put(`/invoices/${invId}`, { status: "paid" });
      setInvoices((prev) => prev.map((inv) => inv.id === invId ? { ...inv, status: "paid" } : inv));
      toast.success("Invoice marked as paid");
    } catch { toast.error("Failed to update invoice"); }
  };

  const resendInvoice = async (invId) => {
    try {
      await api.post(`/invoices/${invId}/send`);
      setInvoices((prev) => prev.map((inv) => inv.id === invId ? { ...inv, status: "sent" } : inv));
      toast.success("Invoice re-sent");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send"); }
  };

  if (loading) return <Layout><div className="p-8 text-sm" style={{ color: "#A8A29E" }}>Loading...</div></Layout>;
  if (!client) return null;

  const activeCycle = cycles.find((c) => c.status === "active");

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="client-detail-page">
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate("/clients")} className="p-1.5 rounded-lg hover:bg-white" style={{ color: "#A8A29E" }}>
            <ArrowLeft size={18} strokeWidth={1.5} />
          </button>
          <div className="relative">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg overflow-hidden"
              style={{ background: form.logo_image ? "transparent" : "#D97706" }}
            >
              {form.logo_image
                ? <img src={form.logo_image} alt="logo" className="w-full h-full object-cover" />
                : client.name?.[0]?.toUpperCase()}
            </div>
            <button
              data-testid="upload-client-logo-button"
              onClick={() => logoRef.current?.click()}
              className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full border-2 border-white flex items-center justify-center"
              style={{ background: "#78716C" }}
              title="Upload logo"
            >
              <Camera size={9} style={{ color: "#fff" }} />
            </button>
            <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
          </div>
          <div>
            <h1 className="font-serif text-2xl font-bold" style={{ color: "#292524" }}>{client.name}</h1>
            <p className="text-sm" style={{ color: "#57534E" }}>{client.email}</p>
          </div>
        </div>

        <Tabs defaultValue="overview">
          <TabsList className="mb-6" style={{ background: "#F5F3EF" }}>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="users">Users ({users.length})</TabsTrigger>
            <TabsTrigger value="requests">Tasks ({requests.length})</TabsTrigger>
            <TabsTrigger value="billing">Billing</TabsTrigger>
            <TabsTrigger value="messages" data-testid="client-messages-tab">
              Messages {clientMessages.filter(m => m.sender_type === "client" && !m.read_by_agency).length > 0 && (
                <span className="ml-1 w-4 h-4 rounded-full text-white text-xs flex items-center justify-center" style={{ background: "#D97706", fontSize: "10px" }}>
                  {clientMessages.filter(m => m.sender_type === "client" && !m.read_by_agency).length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Overview — always editable */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                <div className="bg-white rounded-xl border p-6 space-y-4" style={{ borderColor: "#E7E5E4" }}>
                  <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>Client Details</p>
                  <div className="grid grid-cols-2 gap-4">
                    <FieldBlock label="Organisation Name">
                      <input className={inpCls} style={inpStyle} value={form.name || ""}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        onBlur={() => saveBasic({ name: form.name })} />
                    </FieldBlock>
                    <FieldBlock label="Contact Email">
                      <input className={inpCls} style={inpStyle} type="email" value={form.email || ""}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        onBlur={() => saveBasic({ email: form.email })} />
                    </FieldBlock>
                    <FieldBlock label="Phone">
                      <input className={inpCls} style={inpStyle} value={form.phone || ""}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        onBlur={() => saveBasic({ phone: form.phone || null })} />
                    </FieldBlock>
                    <FieldBlock label="Account Manager">
                      <select value={form.client_manager_id || ""} className={inpCls} style={inpStyle}
                        data-testid="account-manager-select"
                        onChange={(e) => { setForm({ ...form, client_manager_id: e.target.value }); saveBasic({ client_manager_id: e.target.value || null }); }}>
                        <option value="">None</option>
                        {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                    </FieldBlock>
                  </div>
                  <FieldBlock label="Address">
                    <input className={inpCls} style={inpStyle} value={form.address || ""}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      onBlur={() => saveBasic({ address: form.address || null })} />
                  </FieldBlock>
                </div>

                <div className="bg-white rounded-xl border p-6 space-y-4" style={{ borderColor: "#E7E5E4" }}>
                  <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>Billing Configuration</p>
                  <div className="grid grid-cols-2 gap-4">
                    <FieldBlock label="Hourly Rate (£)">
                      <input className={inpCls} style={inpStyle} type="number" min="0" step="0.01" value={form.hourly_rate ?? ""}
                        onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} onBlur={saveBilling} />
                    </FieldBlock>
                    <FieldBlock label="Retained Hours / Month">
                      <input className={inpCls} style={inpStyle} type="number" min="0" step="0.5" value={form.retained_hours ?? ""}
                        onChange={(e) => setForm({ ...form, retained_hours: e.target.value })} onBlur={saveBilling} />
                    </FieldBlock>
                    <FieldBlock label="Additional Hours Rate (£)">
                      <input className={inpCls} style={inpStyle} type="number" min="0" step="0.01" value={form.additional_rate ?? ""}
                        placeholder="Same as hourly"
                        onChange={(e) => setForm({ ...form, additional_rate: e.target.value })} onBlur={saveBilling} />
                    </FieldBlock>
                    <FieldBlock label="Billing Cycle Day">
                      <input className={inpCls} style={inpStyle} type="number" min="1" max="28" value={form.billing_cycle_day ?? 1}
                        onChange={(e) => setForm({ ...form, billing_cycle_day: e.target.value })} onBlur={saveBilling} />
                    </FieldBlock>
                    <FieldBlock label="Invoice Sender Name">
                      <input className={inpCls} style={inpStyle} value={form.invoice_sender_name || ""}
                        placeholder="e.g. Sarah at Balbis"
                        onChange={(e) => setForm({ ...form, invoice_sender_name: e.target.value })}
                        onBlur={saveBilling} />
                    </FieldBlock>
                  </div>
                </div>

                <div className="bg-white rounded-xl border p-6 space-y-4" style={{ borderColor: "#E7E5E4" }}>
                  <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>Billing Address</p>
                  <div className="grid grid-cols-2 gap-4">
                    <FieldBlock label="Address Line 1">
                      <input className={inpCls} style={inpStyle} value={form.ba_line1 || ""}
                        placeholder="123 Business Street"
                        onChange={(e) => setForm({ ...form, ba_line1: e.target.value })}
                        onBlur={saveBillingAddress} />
                    </FieldBlock>
                    <FieldBlock label="Address Line 2">
                      <input className={inpCls} style={inpStyle} value={form.ba_line2 || ""}
                        placeholder="Suite 100 (optional)"
                        onChange={(e) => setForm({ ...form, ba_line2: e.target.value })}
                        onBlur={saveBillingAddress} />
                    </FieldBlock>
                    <FieldBlock label="City">
                      <input className={inpCls} style={inpStyle} value={form.ba_city || ""}
                        placeholder="London"
                        onChange={(e) => setForm({ ...form, ba_city: e.target.value })}
                        onBlur={saveBillingAddress} />
                    </FieldBlock>
                    <FieldBlock label="Postcode">
                      <input className={inpCls} style={inpStyle} value={form.ba_postcode || ""}
                        placeholder="EC1A 1BB"
                        onChange={(e) => setForm({ ...form, ba_postcode: e.target.value })}
                        onBlur={saveBillingAddress} />
                    </FieldBlock>
                    <FieldBlock label="Country">
                      <input className={inpCls} style={inpStyle} value={form.ba_country || ""}
                        placeholder="UK"
                        onChange={(e) => setForm({ ...form, ba_country: e.target.value })}
                        onBlur={saveBillingAddress} />
                    </FieldBlock>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border p-6" style={{ borderColor: "#E7E5E4" }}>
                <p className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: "#A8A29E" }}>Current Cycle Summary</p>
                {activeCycle ? (
                  <div className="space-y-2">
                    <p className="text-xs" style={{ color: "#A8A29E" }}>{activeCycle.start_date?.slice(0, 10)} — {activeCycle.end_date?.slice(0, 10)}</p>
                    <div className="flex justify-between text-sm"><span style={{ color: "#57534E" }}>Retained</span><span className="font-medium" style={{ color: "#292524" }}>{activeCycle.retained_hours}h</span></div>
                    <div className="flex justify-between text-sm"><span style={{ color: "#57534E" }}>Logged</span><span className="font-medium" style={{ color: "#292524" }}>{(activeCycle.total_hours_logged || 0).toFixed(1)}h</span></div>
                    {activeCycle.additional_hours > 0 && (
                      <div className="flex justify-between text-sm"><span style={{ color: "#DC2626" }}>Additional</span><span className="font-medium" style={{ color: "#DC2626" }}>+{activeCycle.additional_hours.toFixed(1)}h</span></div>
                    )}
                    {activeCycle.retained_hours > 0 && activeCycle.total_hours_logged > 0 && (
                      <div className="mt-3">
                        <div className="h-2 rounded-full overflow-hidden" style={{ background: "#E7E5E4" }}>
                          <div className="h-full rounded-full" style={{ width: `${Math.min(100, activeCycle.total_hours_logged / activeCycle.retained_hours * 100)}%`, background: activeCycle.additional_hours > 0 ? "#DC2626" : "#D97706" }} />
                        </div>
                      </div>
                    )}
                    <div className="flex justify-between text-sm pt-2">
                      <span style={{ color: "#57534E" }}>Rate</span>
                      <span className="font-medium" style={{ color: "#292524" }}>£{client.billing_config?.hourly_rate || 0}/hr</span>
                    </div>
                  </div>
                ) : <p className="text-sm" style={{ color: "#A8A29E" }}>No active billing cycle</p>}
              </div>
            </div>
          </TabsContent>

          {/* Users */}
          <TabsContent value="users">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm" style={{ color: "#57534E" }}>{users.length} user{users.length !== 1 ? "s" : ""}</p>
              <Button data-testid="add-user-button" size="sm" onClick={() => setShowUserDialog(true)} style={{ background: "#292524", color: "#fff" }}>
                <Plus size={14} className="mr-1" /> Add User
              </Button>
            </div>
            <div className="space-y-2">
              {users.map((u) => (
                <div key={u.id}
                  data-testid={`client-user-row-${u.id}`}
                  className="bg-white rounded-xl border p-4 flex items-center justify-between cursor-pointer hover:shadow-md transition-shadow group"
                  style={{ borderColor: "#E7E5E4" }}
                  onClick={() => openEditUser(u)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold overflow-hidden"
                      style={{ background: u.avatar_image ? "transparent" : (u.avatar_color || "#D97706") }}>
                      {u.avatar_image ? <img src={u.avatar_image} alt="" className="w-full h-full object-cover" /> : u.name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium" style={{ color: "#292524" }}>{u.name}</p>
                      <p className="text-xs" style={{ color: "#A8A29E" }}>{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-2.5 py-1 rounded-full" style={{ background: u.role === "client_lead" ? "#FEF3C7" : "#F5F3EF", color: u.role === "client_lead" ? "#D97706" : "#57534E" }}>
                      {u.role === "client_lead" ? "Lead" : "Member"}
                    </span>
                    <button
                      data-testid={`delete-client-user-${u.id}`}
                      onClick={(e) => { e.stopPropagation(); e.preventDefault(); setEditUser(null); setDeleteUserTarget(u); }}
                      className="p-1.5 rounded opacity-0 group-hover:opacity-100 hover:bg-red-50 transition-opacity"
                      style={{ color: "#DC2626" }}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
              {users.length === 0 && <p className="text-sm text-center py-8" style={{ color: "#A8A29E" }}>No users yet</p>}
            </div>
          </TabsContent>

          {/* Requests */}
          <TabsContent value="requests">
            <div className="space-y-2">
              {requests.map((req) => {
                const sc = statusColors[req.status] || { bg: "#F5F3EF", text: "#57534E" };
                return (
                  <Link key={req.id} to={`/requests/${req.id}`} className="block bg-white rounded-xl border p-4 hover:shadow-md transition-shadow" style={{ borderColor: "#E7E5E4" }}>
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium" style={{ color: "#292524" }}>{req.title}</p>
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: sc.bg, color: sc.text }}>{fmt(req.status)}</span>
                    </div>
                    <p className="text-xs mt-1" style={{ color: "#A8A29E" }}>{req.created_at?.slice(0, 10)}</p>
                  </Link>
                );
              })}
              {requests.length === 0 && <p className="text-sm text-center py-8" style={{ color: "#A8A29E" }}>No tasks yet</p>}
            </div>
          </TabsContent>

          {/* Billing — with invoice links */}
          <TabsContent value="billing">
            <div className="space-y-4">
              {cycles.map((cyc) => {
                const sc = cycleStatusColors[cyc.status] || { bg: "#F5F3EF", text: "#57534E" };
                const inv = invoices.find((inv) => inv.billing_cycle_id === cyc.id);
                const additionalHours = cyc.additional_hours || 0;
                return (
                  <div key={cyc.id} className="bg-white rounded-xl border p-5" style={{ borderColor: "#E7E5E4" }}>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-medium" style={{ color: "#292524" }}>
                          {cyc.start_date?.slice(0, 10)} — {cyc.end_date?.slice(0, 10)}
                        </p>
                      </div>
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: sc.bg, color: sc.text }}>
                        {cyc.status === "invoiced" ? "Invoiced" : cyc.status === "active" ? "Active" : "Closed"}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                      <div><p className="text-xs" style={{ color: "#A8A29E" }}>Retained</p><p className="font-medium" style={{ color: "#292524" }}>{cyc.retained_hours}h</p></div>
                      <div><p className="text-xs" style={{ color: "#A8A29E" }}>Logged</p><p className="font-medium" style={{ color: "#292524" }}>{(cyc.total_hours_logged || 0).toFixed(1)}h</p></div>
                      <div><p className="text-xs" style={{ color: "#A8A29E" }}>Additional</p><p className="font-medium" style={{ color: additionalHours > 0 ? "#DC2626" : "#292524" }}>{additionalHours > 0 ? `+${additionalHours.toFixed(1)}h` : "0h"}</p></div>
                    </div>
                    {inv && (
                      <div className="pt-3 border-t" style={{ borderColor: "#E7E5E4" }}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-semibold" style={{ color: "#A8A29E" }}>Invoice</p>
                            <p className="text-sm font-medium" style={{ color: "#292524" }}>
                              {inv.invoice_number} · <span className="font-serif">£{inv.total?.toFixed(2)}</span>
                              <span className="ml-2 text-xs px-2 py-0.5 rounded-full" style={{ background: inv.status === "paid" ? "#D1FAE5" : inv.status === "sent" ? "#DBEAFE" : "#F5F3EF", color: inv.status === "paid" ? "#059669" : inv.status === "sent" ? "#1D4ED8" : "#57534E" }}>
                                {inv.status === "paid" ? "Paid" : inv.status === "sent" ? "Sent" : "Draft"}
                              </span>
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" onClick={() => navigate(`/invoices/${inv.id}`)}>
                              <Eye size={13} className="mr-1" />View
                            </Button>
                            {inv.status !== "paid" && (
                              <Button size="sm" variant="outline" onClick={() => markInvoicePaid(inv.id)} style={{ color: "#059669", borderColor: "#059669" }}>
                                <CheckCircle size={13} className="mr-1" />Mark Paid
                              </Button>
                            )}
                            {inv.status === "sent" && (
                              <Button size="sm" variant="outline" onClick={() => resendInvoice(inv.id)}>
                                <Send size={13} className="mr-1" />Re-send
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`delete-invoice-btn-${inv.id}`}
                              onClick={() => setDeleteInvoiceTarget(inv)}
                              style={{ color: "#DC2626", borderColor: "#DC2626" }}
                            >
                              <Trash2 size={13} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                    {!inv && (cyc.status === "closed") && (
                      <div className="pt-3 border-t" style={{ borderColor: "#E7E5E4" }}>
                        <Button size="sm" onClick={() => navigate(`/invoices?cycle=${cyc.id}`)} style={{ background: "#292524", color: "#fff" }}>
                          Generate Invoice
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
              {cycles.length === 0 && <p className="text-sm text-center py-8" style={{ color: "#A8A29E" }}>No billing cycles yet</p>}
            </div>
          </TabsContent>

          {/* Messages */}
          <TabsContent value="messages">
            <ClientMessagesTab clientId={id} />
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent data-testid="add-user-dialog">
          <DialogHeader><DialogTitle className="font-serif">Add Client User</DialogTitle></DialogHeader>
          <form onSubmit={handleAddUser} className="space-y-3">
            <div><Label>Name *</Label><Input data-testid="user-name-input" required value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} /></div>
            <div><Label>Email *</Label><Input data-testid="user-email-input" type="email" required value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} /></div>
            <div><Label>Password *</Label><Input data-testid="user-password-input" type="password" required value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} /></div>
            <div>
              <Label>Role</Label>
              <select value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "#E7E5E4", background: "#fff" }}>
                <option value="client_lead">Client Lead</option>
                <option value="client_user">Client User</option>
              </select>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <input
                data-testid="client-user-notify-checkbox"
                type="checkbox"
                id="client-notify"
                checked={userForm.send_welcome_email}
                onChange={(e) => setUserForm({ ...userForm, send_welcome_email: e.target.checked })}
                className="h-4 w-4 rounded"
              />
              <label htmlFor="client-notify" className="text-sm cursor-pointer" style={{ color: "#57534E" }}>
                Send welcome email with login details
              </label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowUserDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} style={{ background: "#292524", color: "#fff" }}>{saving ? "Adding..." : "Add User"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle className="font-serif">Edit User</DialogTitle></DialogHeader>
          <form onSubmit={handleEditUser} className="space-y-3">
            <div><Label>Name *</Label><Input data-testid="edit-user-name-input" required value={editUserForm.name} onChange={(e) => setEditUserForm({ ...editUserForm, name: e.target.value })} /></div>
            <div><Label>Email *</Label><Input data-testid="edit-user-email-input" type="email" required value={editUserForm.email} onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })} /></div>
            <div>
              <Label>Role</Label>
              <select value={editUserForm.role} onChange={(e) => setEditUserForm({ ...editUserForm, role: e.target.value })} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "#E7E5E4", background: "#fff" }}>
                <option value="client_lead">Client Lead</option>
                <option value="client_user">Client User</option>
              </select>
            </div>
            <div><Label>New Password (optional)</Label><Input data-testid="edit-user-password-input" type="password" value={editUserForm.password} onChange={(e) => setEditUserForm({ ...editUserForm, password: e.target.value })} placeholder="Leave blank to keep current" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditUser(null)}>Cancel</Button>
              <Button type="submit" disabled={editUserSaving} style={{ background: "#292524", color: "#fff" }}>{editUserSaving ? "Saving..." : "Save Changes"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete user confirmation */}
      <AlertDialog open={!!deleteUserTarget} onOpenChange={(o) => !o && setDeleteUserTarget(null)}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{deleteUserTarget?.name}</strong>? They will lose access to the client portal. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-user-confirm"
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Remove User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteInvoiceTarget} onOpenChange={(o) => !o && setDeleteInvoiceTarget(null)}>
        <AlertDialogContent data-testid="delete-invoice-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete invoice <strong>{deleteInvoiceTarget?.invoice_number}</strong>
              {deleteInvoiceTarget?.status === "paid" && " (marked as paid)"}? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-invoice-btn"
              onClick={handleDeleteInvoice}
              style={{ background: "#DC2626", color: "#fff" }}
            >
              Delete Invoice
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
